import { TDrawEntry, TDrawResult } from '@daymade/api-contracts';
import { GameType } from '@daymade/common-lib/dbEnums';
import GameSelector, {
  gameIdFromTabName,
  isMainLotteryDraw,
  isWeeklyDraw,
  tokenTownSelected,
} from 'components/how-it-works/GameSelector';
import MainLayout from 'components/layout/MainLayout';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import PageTitle from 'components/PageTitle';
import FollowUsOnInstagramGreenPageSection from 'components/results/FollowUsOnInstagramGreenPageSection';
import NoResultsMessage from 'components/results/NoResultsMessage';
import PreviousResults from 'components/results/PreviousResults';
import { ResultsGoodToKnow } from 'components/results/ResultsGoodToKnow';
import TokenTownResults from 'components/results/TokenTownResults';
import RevealAnimation from 'components/reveal/RevealAnimation';
import Subheading from 'components/Subheading';
import WinnersGreenPageSection from 'components/WinnersGreenPageSection';
import { FRIDAY, TUESDAY } from 'constants/Draw';
import { Game } from 'constants/Game';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { drawApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';

const Results = ({
  latestDrawResult,
  usingBonusBall,
}: {
  latestDrawResult: TDrawResult;
  usingBonusBall: boolean;
}) => {
  const router = useRouter();

  const [drawResults, setDrawResults] = useState<TDrawResult[]>();
  const [selectedGameId, setSelectedGameId] = useState<string | undefined>(
    gameIdFromTabName(
      router.query?.tab
        ? (router.query?.tab as string)
        : Game.WeeklyDraw.tabName
    )
  );
  const [loadingDraw, setLoadingDraw] = useState<boolean>(false);

  const [revealAnimationResult, setRevealAnimationResult] =
    useState(latestDrawResult);
  const [revealAnimationEntries, setRevealAnimationEntries] =
    useState<TDrawEntry[]>();
  const [latestDrawEntries, setLatestDrawEntries] = useState<TDrawEntry[]>();
  const [secondLatestDrawResult, setSecondLatestDrawResult] =
    useState<TDrawResult>(); // So we can display the results reveal animation for both the Tuesday and the Friday draw
  const [secondLatestDrawEntries, setSecondLatestDrawEntries] = useState<
    TDrawEntry[]
  >([]);

  useEffect(() => {
    const loadAsync = async () => {
      const drawEntries =
        await drawApiClient.auth.getDrawEntriesWithRealAndHypotheticalPrizes({
          params: {
            draw_id: latestDrawResult.draw.id,
          },
        });

      if (drawEntries.status === HttpStatusCode.OK) {
        setLatestDrawEntries(drawEntries.body);
        setRevealAnimationEntries(drawEntries.body);
      }
    };

    loadAsync();
  }, [latestDrawResult.draw.id]);

  const handleToggleDrawCode = async () => {
    const previousDrawCode = revealAnimationResult?.drawSchedule.code;
    const currentDrawCode = previousDrawCode === FRIDAY ? TUESDAY : FRIDAY;

    if (latestDrawResult?.draw.result?.type != GameType.LOTTERY) {
      return;
    }
    if (currentDrawCode === latestDrawResult.drawSchedule.code) {
      setRevealAnimationResult(latestDrawResult);
      if (latestDrawEntries) {
        setRevealAnimationEntries(latestDrawEntries);
      }
    } else if (
      secondLatestDrawResult &&
      currentDrawCode === secondLatestDrawResult.drawSchedule.code
    ) {
      setRevealAnimationResult(secondLatestDrawResult);
      setRevealAnimationEntries(secondLatestDrawEntries);
    } else {
      // Fetch the penultimate draw result and entries (only necessary the first time the user toggles the switch)
      setLoadingDraw(true);
      setRevealAnimationResult({
        draw: {
          ...latestDrawResult.draw,
          result: {
            type: GameType.LOTTERY,
            main: Array(latestDrawResult?.draw.result?.main?.length ?? 4).fill(
              ''
            ),
            bonus: Array(latestDrawResult.draw.result?.bonus.length ?? 1).fill(
              ''
            ),
          },
        },
        drawSchedule: {
          ...latestDrawResult.drawSchedule,
          code: currentDrawCode,
        },
      }); // To ensure the switch moves as soon as the user clicks on it
      const secondLastDrawResponse = await drawApiClient.public.getDrawResult({
        query: {
          draw_code: currentDrawCode,
        },
      });

      if (secondLastDrawResponse.status !== HttpStatusCode.OK) {
        return;
      }
      const secondLastDrawResult = secondLastDrawResponse.body;

      setSecondLatestDrawResult(secondLastDrawResult); // Save this draw result so we don't fetch it again if the user keeps toggling the switch
      setRevealAnimationResult(secondLastDrawResult);

      const secondLastDrawEntries =
        await drawApiClient.auth.getDrawEntriesWithRealAndHypotheticalPrizes({
          params: {
            draw_id: secondLastDrawResult.draw.id,
          },
        });

      if (secondLastDrawEntries.status === HttpStatusCode.OK) {
        setSecondLatestDrawEntries(secondLastDrawEntries.body);
        setRevealAnimationEntries(secondLastDrawEntries.body);
      }
    }
  };

  const getDrawResultsForWeeklyDraw = useCallback(
    async (drawCode?: string, limit?: number) => {
      let results: { drawResults: TDrawResult[] } = {
        drawResults: [],
      };

      if (selectedGameId === Game.WeeklyDraw.id) {
        const drawResultsResponse = await drawApiClient.public.getDrawResults({
          query: {
            limit,
            draw_code: drawCode ? drawCode : FRIDAY,
          },
        });

        if (drawResultsResponse.status === HttpStatusCode.OK) {
          results = drawResultsResponse.body;
        }
      }
      return results.drawResults;
    },
    [selectedGameId]
  );

  useEffect(() => {
    const loadAsyncResults = async () => {
      const results = await getDrawResultsForWeeklyDraw();
      setDrawResults(results);
    };

    loadAsyncResults();
  }, [setDrawResults, getDrawResultsForWeeklyDraw]);

  return (
    <MainLayout additionalClassName="results">
      <NextSeo
        title="Holiday Giveaway | Win Epic Breaks and Adventures | DAYMADE"
        description="Win the trip of your dreams with DAYMADE's holiday giveaway. From relaxing beach breaks to unforgettable adventures. Find out more for a chance of winning."
        openGraph={{
          title: 'Holiday Giveaway | Win Epic Breaks and Adventures | DAYMADE',
          description:
            "Win the trip of your dreams with DAYMADE's holiday giveaway. From relaxing beach breaks to unforgettable adventures. Find out more for a chance of winning.",
        }}
      />

      <PageTitle>
        The <strong>results</strong>
      </PageTitle>

      <GameSelector
        selectedGameId={selectedGameId}
        setSelectedGameId={async (gameId: string) => {
          setDrawResults([]);
          setSelectedGameId(gameId);
        }}
        hiddenSections={[
          Game.DailyPoundRaffle.id,
          Game.DailyChallenger.id,
          Game.IcelandRaffle.id,
          Game.OtherRaffles.id,
        ]}
      />

      {isMainLotteryDraw(selectedGameId) && (
        <PaddedMaxWidthContainer>
          <Subheading style={{ margin: '1em 0 0.5em' }}>
            Winning <strong>numbers</strong>
          </Subheading>
          <RevealAnimation
            // @ts-ignore - TL no idea how this works
            entries={revealAnimationEntries}
            drawResult={revealAnimationResult}
            drawInProgress={false}
            usingBonusBall={usingBonusBall}
            handleToggleDrawCode={handleToggleDrawCode}
          />
        </PaddedMaxWidthContainer>
      )}

      {isWeeklyDraw(selectedGameId) &&
        drawResults &&
        drawResults.length === 0 && <NoResultsMessage />}

      {isWeeklyDraw(selectedGameId) && <FollowUsOnInstagramGreenPageSection />}

      {!tokenTownSelected(selectedGameId) &&
        drawResults &&
        drawResults.length >= 2 && (
          <PreviousResults
            drawResults={drawResults}
            setDrawResults={setDrawResults}
            getDrawResultsForWeeklyDraw={getDrawResultsForWeeklyDraw}
          />
        )}

      {tokenTownSelected(selectedGameId) && <TokenTownResults />}

      {isWeeklyDraw(selectedGameId) && (
        <>
          <WinnersGreenPageSection />
          <ResultsGoodToKnow />
        </>
      )}

      <style jsx>
        {`
          .latestRaffleResult {
            padding: 1em 0;
          }

          :global(.ui.icon.info.message) {
            max-width: 700px;
            margin-bottom: 20px;
          }

          :global(.footer) {
            margin-top: 0 !important;
          }

          @media (min-width: 800px) {
            :global(.results h4) {
              font-size: 1.2em;
            }

            :global(.results .subheading) {
              font-size: 2.5em;
            }
          }

          @media (min-width: 1000px) {
            :global(.results h2.subheading) {
              font-size: 2.7em;
            }

            :global(.results h4) {
              font-size: 1.3em;
            }

            :global(.results p) {
              font-size: 1.2em;
            }
          }
        `}
      </style>
    </MainLayout>
  );
};

export const getServerSideProps = async () => {
  const latestDrawResult = await drawApiClient.public.getDrawResult();

  let drawResult: TDrawResult | null = null;

  if (latestDrawResult.status === HttpStatusCode.OK) {
    drawResult = JSON.parse(JSON.stringify(latestDrawResult.body));
  }

  let usingBonusBall: boolean = false;

  if (drawResult?.draw.id) {
    const drawRanges = await drawApiClient.public.getDrawRangesByDrawId({
      params: { draw_id: drawResult?.draw.id },
    });

    if (latestDrawResult.status === HttpStatusCode.OK) {
      usingBonusBall = JSON.parse(
        JSON.stringify(drawRanges.body.usingBonusBall)
      );
    }
  }

  return {
    props: {
      latestDrawResult: drawResult,
      usingBonusBall,
    },
  };
};

export default Results;
